/**
 * Generate emotionally engaging category titles
 * Removes "Discover" and creates attention-grabbing, emotion-driven headlines
 */

export const getCategoryTitle = (categoryName: string): string => {
  const category = categoryName.toLowerCase();
  
  const titleMap: { [key: string]: string } = {
    'safari': 'Witness Africa\'s Wild Heart',
    'adventure': 'Embrace the Thrill of Adventure',
    'cultural': 'Immerse Yourself in Living Traditions',
    'beach': 'Escape to Paradise Shores',
    'luxury': 'Indulge in Unforgettable Luxury',
    'photography': 'Capture Moments That Last Forever',
    'wellness': 'Rejuvenate Your Mind, Body & Soul',
    'conservation': 'Be Part of Something Greater',
    'history': 'Walk Through Living History',
    'water sports': 'Dive Into Crystal Clear Adventures',
    'family': 'Create Memories Your Family Will Treasure',
    'spiritual': 'Find Peace in Sacred Spaces',
    'honeymoon': 'Begin Your Forever Together',
    'migration': 'Witness Nature\'s Greatest Spectacle',
  };
  
  return titleMap[category] || `Experience ${categoryName}`;
};

export const getCategorySubtitle = (categoryName: string): string => {
  const category = categoryName.toLowerCase();
  
  const subtitleMap: { [key: string]: string } = {
    'safari': 'Where every moment is a once-in-a-lifetime encounter',
    'adventure': 'Push your limits and create stories worth telling',
    'cultural': 'Connect with communities and traditions that span generations',
    'beach': 'Where turquoise waters meet endless horizons',
    'luxury': 'Where every detail is crafted for perfection',
    'photography': 'Frame the world through a lens of wonder',
    'wellness': 'Find balance and harmony in nature\'s embrace',
    'conservation': 'Make a difference while exploring the extraordinary',
    'history': 'Where ancient stories come alive',
    'water sports': 'Feel the ocean\'s pulse beneath you',
    'family': 'Adventures that bring your loved ones closer',
    'spiritual': 'Journey inward while exploring outward',
    'honeymoon': 'Romance redefined in breathtaking settings',
    'migration': 'Stand in awe of nature\'s incredible journey',
  };
  
  return subtitleMap[category] || `Unforgettable ${categoryName.toLowerCase()} experiences await`;
};

export const getCategoryDescription = (categoryName: string): string => {
  const category = categoryName.toLowerCase();
  
  const descriptionMap: { [key: string]: string } = {
    'safari': 'Step into the wild and witness nature\'s most magnificent creatures in their natural habitat. Our carefully curated safari experiences bring you face-to-face with the Big Five, breathtaking landscapes, and moments that will stay with you forever.',
    'adventure': 'For those who seek the extraordinary, our adventure tours push boundaries and ignite your spirit. From mountain peaks to rushing rivers, every journey is designed to challenge, inspire, and transform.',
    'cultural': 'Connect with the heart and soul of Kenya through authentic cultural experiences. Meet local communities, learn ancient traditions, and create meaningful connections that transcend borders.',
    'beach': 'Unwind on pristine shores where time stands still. Our beach escapes offer the perfect blend of relaxation, adventure, and tropical paradise.',
    'luxury': 'Indulge in the finest accommodations, personalized service, and exclusive experiences. Every moment is crafted to exceed your highest expectations.',
    'photography': 'Capture the world\'s most stunning moments with expert guidance. From golden hour game drives to intimate cultural portraits, create a portfolio of unforgettable images.',
    'wellness': 'Reconnect with yourself in serene settings designed for healing and renewal. Our wellness retreats combine yoga, meditation, and nature\'s restorative power.',
    'conservation': 'Join meaningful conservation efforts while experiencing Kenya\'s incredible wildlife. Your journey contributes to protecting these treasures for future generations.',
    'history': 'Walk in the footsteps of explorers, traders, and ancient civilizations. Uncover the rich tapestry of stories that shaped this remarkable land.',
    'water sports': 'Dive into crystal-clear waters teeming with life. From snorkeling vibrant coral reefs to thrilling water adventures, experience the ocean like never before.',
    'family': 'Create lasting memories with adventures designed for all ages. Safe, educational, and fun experiences that bring families closer together.',
    'spiritual': 'Find peace and reflection in sacred spaces that have inspired generations. Connect with your inner self while exploring Kenya\'s spiritual heritage.',
    'honeymoon': 'Begin your journey together in the most romantic settings. Intimate experiences, stunning sunsets, and moments that celebrate your love.',
    'migration': 'Witness one of nature\'s greatest spectacles as millions of animals embark on their epic journey. A once-in-a-lifetime experience that will leave you in awe.',
  };
  
  return descriptionMap[category] || `Experience the best of ${categoryName.toLowerCase()} with our carefully curated packages.`;
};

